import React, { useEffect, useState } from 'react';
import { omitThemingProps, styled, th, useStyleConfig, vui } from '../core';
import Icon from '../icon';
import T from '../t';
import { cs, filterUndefined } from '../utils';
import { checkboxColors } from './consts';
import { useCheckboxGroupContext } from './context';
const CheckboxControl = styled.spanBox `
  border-radius: none;
  display: inline-flex;
  flex-shrink: 0;
  position: relative;
  transition-duration: fast;
`;
const CheckboxInput = styled.input `
  cursor: inherit;
  height: 100%;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`;
export const CheckboxBase = styled.labelBox `
  cursor: pointer;
  display: inline-flex;
  width: fit-content;

  &:not([aria-disabled='true']):hover .vui-checkboxControl {
    color: ${p => th.color(p.controlHoverColor)};
  }

  &[aria-disabled='true'] {
    color: ${checkboxColors.disabled};
    cursor: not-allowed;

    .vui-checkboxControl {
      color: ${checkboxColors.disabled};
    }
  }
`;
/**
 * Allows selection of one or more choices from a set of items. Handles controlled and uncontrolled modes.
 * Uses icons to display itself in different states. Can be indeterminate when selecting some nested items.
 */
export const Checkbox = vui((props, ref) => {
    const { defaultValue: groupDefaultValue, ...checkboxGroupProps } = useCheckboxGroupContext() ?? {};
    const mergedProps = { ...checkboxGroupProps, ...props };
    const { checked, children, className, defaultChecked = groupDefaultValue !== undefined ? groupDefaultValue.includes(props.value) : undefined, disabled, icon: iconProp = 'cuiCheckboxUnselected', iconChecked = 'cuiCheckboxSelected', iconIndeterminate = 'cuiCheckboxIndeterminate', id, inputProps, inputRef, isIndeterminate, label, name, onChange, required, value, ...rest } = omitThemingProps(mergedProps);
    const [isChecked, setIsChecked] = useState(defaultChecked);
    const styles = useStyleConfig('Checkbox', mergedProps);
    const { color: controlColor, hoverColor, ...controlStyles } = styles.control;
    const icon = isIndeterminate ? iconIndeterminate : isChecked ? iconChecked : iconProp;
    const controlMr = children || label ? '12px' : 0;
    const color = controlColor ? controlColor : checkboxColors.main;
    const controlHoverColor = hoverColor ? hoverColor : checkboxColors.hover;
    const isLongLabel = label && label?.length >= 60;
    useEffect(() => {
        checked !== undefined && setIsChecked(checked);
    }, [checked]);
    function handleOnChange(e) {
        setIsChecked(e.target.checked);
        onChange?.(e);
    }
    const aliasedProps = filterUndefined({
        'aria-disabled': disabled
    });
    return (React.createElement(CheckboxBase, { alignItems: isLongLabel ? 'flex-start' : 'center', className: cs('vui-checkbox', className), controlHoverColor: controlHoverColor, ref: ref, ...styles.container, ...aliasedProps, ...rest },
        React.createElement(CheckboxControl, { bg: "white", className: "vui-checkboxControl", color: color, focusWithinRing: 3, mr: controlMr, role: "none", ...controlStyles },
            React.createElement(CheckboxInput, { "aria-label": label ? label : 'input-checkbox', className: "vui-checkboxInput", onChange: handleOnChange, ref: inputRef, type: "checkbox", ...{ checked, defaultChecked, disabled, id, name, required, value }, ...inputProps }),
            React.createElement(Icon, { className: "vui-checkboxIcon", h: "100%", name: icon, w: "100%" })),
        children ??
            (label && (React.createElement(T, { className: "vui-checkboxLabel", lineHeight: isLongLabel ? '26px' : 'normal', ...styles.label }, label)))));
});
export default Checkbox;
